import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { 
  getTopQueries, 
  getZeroHitTrends, 
  getBugReportsByStatus, 
  getDashboardStats,
  getAnalyticsQueries,
  getAnalyticsZeroHits,
  getAnalyticsFlaggedBugs
} from '../../services/(adminapi)/chartAPI'

// Async thunks
export const fetchTopQueries = createAsyncThunk(
  'chart/fetchTopQueries',
  async ({ limit = 10, timeRange = '30', tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getTopQueries(limit, timeRange, tenant)
      const payload = response?.data ?? response
      return Array.isArray(payload) ? payload : []
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch top queries')
    }
  }
)

export const fetchZeroHitTrends = createAsyncThunk(
  'chart/fetchZeroHitTrends',
  async ({ days = 30, tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getZeroHitTrends(days, tenant)
      const data = Array.isArray(response) ? response : (response?.data ?? [])
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch zero hit trends')
    }
  }
)

export const fetchBugReportsByStatus = createAsyncThunk(
  'chart/fetchBugReportsByStatus',
  async ({ timeRange = '30', tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getBugReportsByStatus(timeRange, tenant)
      const data = Array.isArray(response) ? response : (response?.data ?? [])
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch bug reports by status')
    }
  }
)

export const fetchDashboardStats = createAsyncThunk(
  'chart/fetchDashboardStats',
  async ({ timeRange = '30', tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getDashboardStats(timeRange, tenant)
      return response.data || {
        totalQueries: 0,
        totalBugReports: 0,
        openBugReports: 0,
        aiHealth: 'healthy',
        successRate: 100
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch dashboard stats')
    }
  }
)

export const fetchAnalyticsQueries = createAsyncThunk(
  'chart/fetchAnalyticsQueries',
  async ({ limit = 10, timeRange = '30', tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getAnalyticsQueries(limit, timeRange, tenant)
      const data = Array.isArray(response) ? response : (response?.data ?? [])
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics queries')
    }
  }
)

export const fetchAnalyticsZeroHits = createAsyncThunk(
  'chart/fetchAnalyticsZeroHits',
  async ({ limit = 10, timeRange = '30', tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getAnalyticsZeroHits(limit, timeRange, tenant)
      const data = Array.isArray(response) ? response : (response?.data ?? [])
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics zero hits')
    }
  }
)

export const fetchAnalyticsFlaggedBugs = createAsyncThunk(
  'chart/fetchAnalyticsFlaggedBugs',
  async ({ limit = 10, timeRange = '30', tenant = 'all' }, { rejectWithValue }) => {
    try {
      const response = await getAnalyticsFlaggedBugs(limit, timeRange, tenant)
      const data = Array.isArray(response) ? response : (response?.data ?? [])
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch analytics flagged bugs')
    }
  }
)

const initialState = {
  topQueries: [],
  zeroHitTrends: [],
  bugReportsByStatus: [],
  dashboardStats: {
    totalQueries: 0,
    totalBugReports: 0,
    openBugReports: 0,
    aiHealth: 'healthy',
    successRate: 100
  },
  analyticsQueries: [],
  analyticsZeroHits: [],
  analyticsFlaggedBugs: [],
  loading: false,
  error: null
}

const chartSlice = createSlice({
  name: 'chart',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Top Queries
      .addCase(fetchTopQueries.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTopQueries.fulfilled, (state, action) => {
        state.loading = false
        state.topQueries = action.payload
        state.error = null
      })
      .addCase(fetchTopQueries.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Zero Hit Trends
      .addCase(fetchZeroHitTrends.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchZeroHitTrends.fulfilled, (state, action) => {
        state.loading = false
        state.zeroHitTrends = action.payload
        state.error = null
      })
      .addCase(fetchZeroHitTrends.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Bug Reports By Status
      .addCase(fetchBugReportsByStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBugReportsByStatus.fulfilled, (state, action) => {
        state.loading = false
        state.bugReportsByStatus = action.payload
        state.error = null
      })
      .addCase(fetchBugReportsByStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Dashboard Stats
      .addCase(fetchDashboardStats.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.loading = false
        state.dashboardStats = action.payload
        state.error = null
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Analytics Queries
      .addCase(fetchAnalyticsQueries.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAnalyticsQueries.fulfilled, (state, action) => {
        state.loading = false
        state.analyticsQueries = action.payload
        state.error = null
      })
      .addCase(fetchAnalyticsQueries.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Analytics Zero Hits
      .addCase(fetchAnalyticsZeroHits.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAnalyticsZeroHits.fulfilled, (state, action) => {
        state.loading = false
        state.analyticsZeroHits = action.payload
        state.error = null
      })
      .addCase(fetchAnalyticsZeroHits.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Analytics Flagged Bugs
      .addCase(fetchAnalyticsFlaggedBugs.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchAnalyticsFlaggedBugs.fulfilled, (state, action) => {
        state.loading = false
        state.analyticsFlaggedBugs = action.payload
        state.error = null
      })
      .addCase(fetchAnalyticsFlaggedBugs.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError } = chartSlice.actions
export default chartSlice.reducer

